import React, { useState } from 'react';
import { Env, ErrorBanner, Spinner } from '../../shared';
import { useUserTransactions } from '../api/useUserTransactions';
import { RefundResponse, TransactionItem, TransactionRedemption } from '../api/types';
import { RefundModal } from './RefundModal';

interface Props {
  env: Env;
  userId: string;
  /** Bubble the post-refund balance up so the user card stays in sync. */
  onBalanceChange?: (balance: number) => void;
  onNotice?: (message: string) => void;
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/** Refunded rows keep the original debit but the redemption is now closed. */
function statusClasses(status: string): string {
  if (status === 'confirmed') return 'bg-emerald-950/50 border-emerald-800/50 text-emerald-300';
  if (status === 'refunded') return 'bg-violet-950/50 border-violet-800/50 text-violet-300';
  if (status === 'expired') return 'bg-gray-800 border-gray-700 text-gray-400';
  return 'bg-gray-800 border-gray-700 text-gray-300';
}

/**
 * Coin-ledger history for one user: paginated, newest first, filterable by
 * source type. A redemption row the backend marks `refundable` gets a Refund
 * action — the 90-day window and the confirmed/coin rules are decided server
 * side, so the button simply reflects them. Refunded and out-of-window
 * redemptions render their state instead of an action.
 */
export function TransactionHistory({ env, userId, onBalanceChange, onNotice }: Props) {
  const tx = useUserTransactions(env, userId);
  const [refundTarget, setRefundTarget] = useState<TransactionRedemption | null>(null);

  const onRefunded = (res: RefundResponse) => {
    onBalanceChange?.(res.wallet_balance);
    onNotice?.(
      res.already_refunded
        ? 'Redemption was already refunded — no second credit applied.'
        : `Refunded ${res.coins_refunded.toLocaleString()} coins.`,
    );
    // Re-read the page: the refund adds an admin_adjust credit row and flips the
    // redemption status, both of which belong in the history immediately.
    tx.reload();
  };

  const refundCell = (item: TransactionItem) => {
    const r = item.redemption;
    if (!r) return null;
    if (r.status === 'refunded') return <span className="text-xs text-violet-300">Refunded</span>;
    if (r.refundable) {
      return (
        <button
          onClick={() => setRefundTarget(r)}
          className="px-2 py-1 text-xs font-medium rounded-md bg-red-950/60 border border-red-800/50 text-red-300 hover:bg-red-900/50"
        >
          Refund
        </button>
      );
    }
    if (r.status === 'confirmed') {
      return (
        <span className="text-xs text-gray-500" title={`Outside the ${tx.refundWindowDays}-day refund window`}>
          Window closed
        </span>
      );
    }
    return <span className="text-xs text-gray-500">{r.status}</span>;
  };

  return (
    <div className="mt-4 pt-4 border-t border-gray-800">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <p className="text-xs font-medium text-gray-400">Transaction history</p>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500" htmlFor={`src-${userId}`}>
            Source
          </label>
          <select
            id={`src-${userId}`}
            value={tx.sourceType}
            onChange={(e) => tx.applyFilter(e.target.value)}
            className="px-2 py-1 bg-gray-950 border border-gray-700 rounded-md text-xs text-white focus:outline-none focus:border-violet-500"
          >
            <option value="">All sources</option>
            {tx.sourceTypes.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      {tx.error && <ErrorBanner message={tx.error} />}

      {tx.loading ? (
        <div className="flex justify-center py-6">
          <Spinner size="sm" />
        </div>
      ) : tx.items.length === 0 ? (
        <p className="text-xs text-gray-500 py-4">
          {tx.sourceType ? `No ${tx.sourceType} transactions.` : 'No transactions yet.'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-500 border-b border-gray-800">
                <th className="text-left font-medium py-2 pr-3">When</th>
                <th className="text-left font-medium py-2 pr-3">Source</th>
                <th className="text-right font-medium py-2 pr-3">Amount</th>
                <th className="text-right font-medium py-2 pr-3">Balance</th>
                <th className="text-left font-medium py-2 pr-3">Redemption</th>
                <th className="text-right font-medium py-2" />
              </tr>
            </thead>
            <tbody>
              {tx.items.map((item) => (
                <tr key={item.id} className="border-b border-gray-800/60">
                  <td className="py-2 pr-3 text-gray-400 whitespace-nowrap">{formatWhen(item.occurred_at)}</td>
                  <td className="py-2 pr-3 text-gray-300">
                    {item.source_type}
                    {typeof item.metadata?.reason_code === 'string' && (
                      <span
                        className="ml-1.5 text-gray-500"
                        title={typeof item.metadata.reason === 'string' ? item.metadata.reason : undefined}
                      >
                        · {item.metadata.reason_code}
                      </span>
                    )}
                  </td>
                  <td
                    className={`py-2 pr-3 text-right tabular-nums font-medium ${
                      item.amount > 0 ? 'text-emerald-300' : 'text-red-300'
                    }`}
                  >
                    {item.amount > 0 ? '+' : ''}
                    {item.amount.toLocaleString()}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums text-gray-400">
                    {item.wallet_after.toLocaleString()}
                  </td>
                  <td className="py-2 pr-3">
                    {item.redemption ? (
                      <span
                        className={`px-1.5 py-0.5 rounded border text-[10px] ${statusClasses(item.redemption.status)}`}
                      >
                        {item.redemption.status}
                      </span>
                    ) : (
                      <span className="text-gray-700">—</span>
                    )}
                  </td>
                  <td className="py-2 text-right">{refundCell(item)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center justify-between mt-3">
        <span className="text-[10px] text-gray-600">
          {tx.items.length > 0 && `rows ${tx.offset + 1}–${tx.offset + tx.items.length}`}
        </span>
        <div className="flex gap-2">
          <button
            onClick={tx.prevPage}
            disabled={tx.offset === 0 || tx.loading}
            className="px-2 py-1 text-xs rounded-md bg-gray-800 text-gray-300 hover:bg-gray-700 disabled:opacity-40"
          >
            Previous
          </button>
          <button
            onClick={tx.nextPage}
            disabled={!tx.hasMore || tx.loading}
            className="px-2 py-1 text-xs rounded-md bg-gray-800 text-gray-300 hover:bg-gray-700 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>

      {refundTarget && (
        <RefundModal
          env={env}
          redemption={refundTarget}
          onClose={() => setRefundTarget(null)}
          onRefunded={onRefunded}
        />
      )}
    </div>
  );
}
